import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  fetchPlaceAutocompleteSuggestions,
  fetchPlaceAddressDetails,
  newSessionToken,
} from "../services/googlePlacesAutocomplete";

const DEBOUNCE_MS = 300;
const DROPDOWN_Z = 20000;

/**
 * Campo de texto con sugerencias Places (New). Al elegir una fila, rellena la calle;
 * con `onPlaceAddressResolved` también se obtiene detalle del lugar (población, provincia, CP).
 */
export default function AddressAutocompleteInput({
  value,
  onValueChange,
  placeholder,
  style,
  autoFocus,
  name,
  id,
  disabled,
  onFocus,
  onBlur,
  onInputKeyDown,
  className,
  "aria-label": ariaLabel,
  /** { latitude, longitude, radiusM? } | null — restringe sugerencias Google Places a esa población */
  placesLocalityContext,
  /** Si se define, al elegir una sugerencia con placeId se llama Places Details y se rellenan calle/nº/población/provincia/CP. */
  onPlaceAddressResolved,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const sessionRef = useRef(newSessionToken());
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const portalRef = useRef(null);
  /** Posición del desplegable en viewport (portal); evita recorte por overflow:hidden/auto en modales). */
  const [ddPos, setDdPos] = useState(null);

  const localityKey = placesLocalityContext
    ? `${placesLocalityContext.latitude},${placesLocalityContext.longitude},${placesLocalityContext.radiusM ?? ""}`
    : "";

  useEffect(() => {
    sessionRef.current = newSessionToken();
  }, [localityKey]);

  const syncDropdownRect = useCallback(() => {
    const el = inputRef.current;
    if (!el) {
      setDdPos(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setDdPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 160) });
  }, []);

  useLayoutEffect(() => {
    if (!open || !items.length) {
      setDdPos(null);
      return;
    }
    syncDropdownRect();
    const onWin = () => syncDropdownRect();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open, items, highlight, value, syncDropdownRect]);

  const runFetch = useCallback(async (text) => {
    if (!text.trim()) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const hasLoc =
        placesLocalityContext &&
        Number.isFinite(placesLocalityContext.latitude) &&
        Number.isFinite(placesLocalityContext.longitude);
      const opts = hasLoc
        ? {
            localityCenter: {
              latitude: placesLocalityContext.latitude,
              longitude: placesLocalityContext.longitude,
            },
            localityRadiusM: placesLocalityContext.radiusM,
          }
        : {};
      const list = await fetchPlaceAutocompleteSuggestions(text, sessionRef.current, opts);
      setItems(list);
      setHighlight(list.length ? 0 : -1);
      // No comprobar activeElement: la petición es async; el foco puede estar en el calendario
      // del mismo modal y aun así debemos mostrar sugerencias al volver al campo.
      setOpen(list.length > 0);
    } finally {
      setLoading(false);
    }
  }, [localityKey]);

  /** Solo búsqueda tras escritura en el input: no disparar al hidratar el formulario con calle ya guardada. */
  const scheduleFetchFromInput = useCallback(
    (text) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        runFetch(text || "");
      }, DEBOUNCE_MS);
    },
    [runFetch],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const pick = useCallback(
    async (item) => {
      const text = typeof item === "string" ? item : item?.text || "";
      const placeId = typeof item === "string" ? null : item?.id;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = null;

      if (placeId && onPlaceAddressResolved && !/^s-\d+$/.test(String(placeId))) {
        try {
          const parts = await fetchPlaceAddressDetails(placeId, sessionRef.current);
          if (
            parts &&
            (parts.calle || parts.poblacion || parts.provincia || parts.codigo_postal || parts.numero)
          ) {
            const calleLine = (parts.calle && parts.calle.trim()) || text;
            onPlaceAddressResolved({
              ...parts,
              calle: calleLine,
              suggestion_text: text,
            });
            sessionRef.current = newSessionToken();
            setItems([]);
            setOpen(false);
            setHighlight(-1);
            return;
          }
        } catch {
          /* continuar con solo texto */
        }
      }

      onValueChange(text);
      sessionRef.current = newSessionToken();
      setItems([]);
      setOpen(false);
      setHighlight(-1);
    },
    [onValueChange, onPlaceAddressResolved],
  );

  const onKeyDown = (e) => {
    if (open && items.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, items.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        return;
      }
      if (e.key === "Enter" && highlight >= 0 && items[highlight]) {
        e.preventDefault();
        pick(items[highlight]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
    onInputKeyDown?.(e);
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        type="text"
        name={name}
        id={id}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        autoFocus={autoFocus}
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={className}
        style={style}
        onChange={(e) => {
          const v = e.target.value;
          onValueChange(v);
          setOpen(true);
          scheduleFetchFromInput(v);
        }}
        onFocus={(e) => {
          onFocus?.(e);
          if (items.length > 0) setOpen(true);
        }}
        onBlur={(e) => {
          onBlur?.(e);
          const related = e.relatedTarget;
          setTimeout(() => {
            const ae = document.activeElement;
            if (portalRef.current && (portalRef.current.contains(ae) || portalRef.current.contains(related))) return;
            if (ae && inputRef.current && inputRef.current.contains(ae)) return;
            setOpen(false);
          }, 280);
        }}
        onKeyDown={onKeyDown}
      />
      {loading && (
        <span
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 13,
            opacity: 0.6,
            pointerEvents: "none",
          }}
        >
          …
        </span>
      )}
      {open && items.length > 0 && ddPos &&
        createPortal(
          <ul
            ref={portalRef}
            data-places-suggest-root
            role="listbox"
            style={{
              position: "fixed",
              top: ddPos.top,
              left: ddPos.left,
              width: ddPos.width,
              margin: 0,
              padding: 0,
              listStyle: "none",
              maxHeight: 240,
              overflowY: "auto",
              zIndex: DROPDOWN_Z,
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.15)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
              background: "#fff",
              color: "#111827",
            }}
          >
            {items.map((it, i) => (
              <li key={it.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    pick(it);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "9px 12px",
                    fontSize: 12,
                    border: "none",
                    borderBottom: i < items.length - 1 ? "1px solid rgba(0,0,0,0.06)" : "none",
                    background: i === highlight ? "rgba(0,0,0,0.06)" : "transparent",
                    cursor: "pointer",
                    color: "#111827",
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={() => setHighlight(i)}
                >
                  {it.text}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
